import React, { useState } from "react";
import { X, Users, Info, Check, Clock, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";

export default function MarketingManagerModal({ open, onClose }) {
    const [step, setStep] = useState("info"); // "info" | "form" | "done"
    const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", password: "" });
    const [loading, setLoading] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/auth/register", { ...form, account_type: "marketing_manager" });
            setStep("done");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Fout");
        }
        setLoading(false);
    };

    const reset = () => { setStep("info"); setForm({ name: "", company: "", email: "", phone: "", password: "" }); };
    const close = () => { reset(); onClose(); };

    return (
        <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={close} data-testid="mm-modal-overlay">
            <div
                className="relative bg-[#FFFFFF] border border-zinc-200 rounded-md max-w-xl w-full my-8 overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                data-testid="mm-modal"
            >
                <button onClick={close} className="absolute top-4 right-4 text-zinc-500 hover:text-zinc-900 z-10" data-testid="mm-modal-close">
                    <X className="h-5 w-5" />
                </button>

                {step === "info" && (
                    <div className="p-8 lg:p-10">
                        <div className="h-12 w-12 rounded-sm bg-[#7C3AED]/15 border border-[#7C3AED]/40 flex items-center justify-center mb-5">
                            <Users className="h-5 w-5 text-[#7C3AED]" />
                        </div>
                        <div className="text-xs uppercase tracking-[0.3em] text-[#7C3AED] mb-2">Marketing Manager</div>
                        <h2 className="font-display font-black text-3xl tracking-tighter leading-tight mb-4 text-zinc-950">
                            Beheer meerdere makelaars vanuit één account.
                        </h2>
                        <p className="text-sm text-zinc-500 leading-relaxed mb-6">
                            Bent u verantwoordelijk voor de marketing van meerdere makelaars binnen één kantoor of groep? Een Marketing Manager account geeft u een centraal overzicht om namens elk van hen te werken.
                        </p>
                        <ul className="space-y-3 mb-8">
                            {[
                                "Volledig overzicht van alle projecten van de aan u gekoppelde makelaars",
                                "Video-aanvragen indienen namens uw makelaars",
                                "Review en download finale video's, reels en AI-content",
                                "Bewerk scripts, plaats opmerkingen op previews en keur opleveringen goed",
                                "Makelaars worden door Social Factory aan uw account gekoppeld na goedkeuring",
                            ].map((t, i) => (
                                <li key={i} className="flex items-start gap-3 text-sm text-zinc-600">
                                    <div className="h-5 w-5 rounded-sm bg-[#7C3AED]/20 border border-[#7C3AED]/40 flex-shrink-0 flex items-center justify-center mt-0.5">
                                        <Check className="h-3 w-3 text-[#7C3AED]" />
                                    </div>
                                    <span>{t}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="flex items-start gap-3 p-4 bg-[#F4F4F5] border border-zinc-200 rounded-sm mb-6">
                            <Info className="h-4 w-4 text-[#E5A93D] flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-zinc-500 leading-relaxed">
                                U kan <span className="text-zinc-800 font-medium">video-aanvragen indienen namens uw gekoppelde makelaars</span>. Elke aanvraag wordt automatisch gelinkt aan de juiste makelaar zodat hij/zij die ook ziet in zijn eigen dashboard.
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <Button onClick={() => setStep("form")} className="flex-1 h-11 bg-[#7C3AED] hover:bg-[#8B5CF6] text-white font-bold tracking-wide rounded-sm active:scale-[0.98]" data-testid="mm-to-form-btn">
                                Aanvraag indienen
                            </Button>
                            <Button onClick={close} variant="ghost" className="flex-1 h-11 border border-zinc-200 text-zinc-600 hover:bg-zinc-100 rounded-sm">
                                Annuleren
                            </Button>
                        </div>
                    </div>
                )}

                {step === "form" && (
                    <form onSubmit={submit} className="p-8 lg:p-10" data-testid="mm-form">
                        <div className="text-xs uppercase tracking-[0.3em] text-[#7C3AED] mb-2">Aanvraag Marketing Manager</div>
                        <h2 className="font-display font-black text-2xl tracking-tighter mb-2 text-zinc-950">Uw gegevens</h2>
                        <p className="text-xs text-zinc-500 mb-6">Na goedkeuring door Social Factory kan u inloggen en worden uw makelaars aan uw account gekoppeld.</p>
                        <div className="space-y-4">
                            <div>
                                <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-1 block">Naam *</Label>
                                <Input required value={form.name} onChange={set("name")} className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#7C3AED] h-11" data-testid="mm-name" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-1 block">Kantoor / Bedrijf *</Label>
                                <Input required value={form.company} onChange={set("company")} placeholder="bv. Immo Smith Groep" className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#7C3AED] h-11" data-testid="mm-company" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-1 block">Email *</Label>
                                <Input type="email" required value={form.email} onChange={set("email")} className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#7C3AED] h-11" data-testid="mm-email" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-1 block">GSM</Label>
                                <Input value={form.phone} onChange={set("phone")} placeholder="+32..." className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#7C3AED] h-11" data-testid="mm-phone" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-1 block">Wachtwoord * (min. 6 tekens)</Label>
                                <Input type="password" required minLength={6} value={form.password} onChange={set("password")} className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#7C3AED] h-11" data-testid="mm-password" />
                            </div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3 mt-8">
                            <Button type="button" onClick={() => setStep("info")} variant="ghost" className="sm:flex-none h-11 border border-zinc-200 text-zinc-500 hover:bg-zinc-100 rounded-sm">
                                Terug
                            </Button>
                            <Button type="submit" disabled={loading} className="flex-1 h-11 bg-[#7C3AED] hover:bg-[#8B5CF6] text-white font-bold tracking-wide rounded-sm active:scale-[0.98]" data-testid="mm-submit">
                                {loading ? "Bezig..." : "Aanvraag versturen"}
                            </Button>
                        </div>
                    </form>
                )}

                {step === "done" && (
                    <div className="p-8 lg:p-10 text-center" data-testid="mm-done">
                        <div className="h-14 w-14 mx-auto rounded-full bg-[#7C3AED]/15 border border-[#7C3AED]/40 flex items-center justify-center mb-5">
                            <Clock className="h-6 w-6 text-[#7C3AED]" />
                        </div>
                        <div className="text-xs uppercase tracking-[0.3em] text-[#7C3AED] mb-2">Aanvraag ontvangen</div>
                        <h2 className="font-display font-black text-2xl tracking-tighter mb-3 text-zinc-950">In afwachting van goedkeuring</h2>
                        <p className="text-sm text-zinc-500 leading-relaxed mb-6">
                            Social Factory beoordeelt uw aanvraag en koppelt de juiste makelaars aan uw account. U krijgt een bevestiging per e-mail.
                        </p>
                        <Button onClick={close} className="h-11 px-8 bg-[#7C3AED] hover:bg-[#8B5CF6] text-white font-bold rounded-sm">
                            Sluiten
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
